import { FC } from 'react';
import { capitalize } from 'lodash-es';
import { BoxV2 as Box, Table, useTable } from 'portal-commons';

import { RcsCampaignListingRow } from '../../components';
import { Loader } from '@/shared_elements';
import { campaignController, CampaignListItem } from '@/apis/core';
import { MaxCampaignCount } from '../../constants';
import { convertTimeWithTimezone, getTimezone } from '@/utils/time';
import { PageTitle } from '@/components';
import { TABLE_HEADERS } from './constants';
import Filters from './Filters';
import useRcsCampaignFilterOptions from './useRcsCampaignFilter';
import { remapTCRStatus } from '../../utils';

const RcsCampaigns: FC = () => {
  const filter = useRcsCampaignFilterOptions();
  const { appliedFilters, additionalFilters } = filter;

  const getQueries = (tableFilter: Record<string, unknown>) => {
    const { createDate, ...restFilters } = appliedFilters;
    const query: Record<string, unknown> = {
      mnoStatus: 'ACTIVE,SUSPENDED,REJECTED,TERMINATED',
      ...tableFilter,
      ...restFilters,
      ...additionalFilters,
      channelId: 'RCS',
      timezone: getTimezone(),
    };
    if (createDate) {
      const dates = createDate.replace(/\s/gi, '').split('-');
      if (dates.length === 2) {
        query.startDate = convertTimeWithTimezone(
          dates[0],
          undefined,
          'YYYY-MM-DD'
        );
        query.endDate = convertTimeWithTimezone(
          dates[1],
          undefined,
          'YYYY-MM-DD'
        );
      }
    }
    return query;
  };

  const table = useTable<CampaignListItem>(
    async (tableFilter) => {
      const queries = getQueries(tableFilter as Record<string, unknown>);

      const response = await campaignController.getCampaigns(queries);

      return {
        data: response?.records || [],
        rowsPerPage: response?.recordsPerPage || 10,
        total: response?.totalRecords || 0,
        page: response?.page || 1,
      };
    },
    {
      cappingTotal: MaxCampaignCount,
      deps: [appliedFilters, additionalFilters],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );

  const displayTitle = (total: number, tcrStatus?: string) => {
    if (tcrStatus) {
      return `${total} ${capitalize(remapTCRStatus(tcrStatus, ''))} Campaigns`;
    }
    return `${total} Campaigns`;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <section className="campaigns-listing-section page-content">
        {table.pageLoading ? (
          <Loader />
        ) : (
          <Box sx={{ width: '100%', mb: 'xs' }}>
            <PageTitle
              note={
                <>
                  Results capped at first{' '}
                  {MaxCampaignCount.toLocaleString('en-US')} records
                </>
              }
            >
              {table.total === 0
                ? 'Campaigns'
                : `${displayTitle(table.total, appliedFilters.status)} `}
            </PageTitle>
            <Filters filter={filter} />
            <Table
              testId="rcsCampaignsTable"
              loading={table.loading}
              handleChangePage={table.handleChangePage}
              createSortHandler={table.handleSorting}
              emptyState="no campaigns to view"
              filter={table.filter}
              headRows={TABLE_HEADERS}
              tableData={table.tableData.map((campaign) => (
                <RcsCampaignListingRow key={campaign.uid} campaign={campaign} />
              ))}
              selectable={false}
              pagination={table.pagination}
            />
          </Box>
        )}
      </section>
    </Box>
  );
};
export default RcsCampaigns;
